import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { ConfigSystemParams, DgAlignment, DgColumnDataType, DgColumnWidths, eSimApiPaths } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { GridViewDefaults } from 'src/app/core/grid-view/component/grid-view.defaults';
import { DgActionColumn } from 'src/app/core/interfaces/datagrid/dg-action-column';
import { DgApiPath } from 'src/app/core/interfaces/datagrid/dg-api-paths';
import { DgColumn } from 'src/app/core/interfaces/datagrid/dg-column';
import { DgPopup } from 'src/app/core/interfaces/datagrid/dg-pop-up';
import { DgProperties } from 'src/app/core/interfaces/datagrid/dg-properties';
import { EncryptionService } from 'src/app/core/services/utelize/general/encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { GridViewComponent } from 'src/app/core/grid-view/component/grid-view.component';
import { GridViewService } from 'src/app/core/services/utelize/general/grid-view.service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';
import { ValidatorService } from 'src/app/core/services/utelize/general/validator-service';

const apiPath             =environment.eSimApi+eSimApiPaths.Orders;
const exportFilenameSuffix="Data-Sessions-Data";
const popUpTitle          ="Data Session";

@Component({
  selector: 'app-sim-data-sessions-embedded',
  templateUrl: './sim-data-sessions.component.html',
  styleUrls: ['./sim-data-sessions.component.scss']
})

export class SimDataSessionsEmbeddedComponent implements AfterViewInit, OnInit {
  
  @Input() orderID: number | undefined;
  
  @ViewChild('gridView', { static: false }) gridView!: GridViewComponent;
  
  get embeddedGridHeight() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.ModalGridHeightEmbedded,       420); }
  get selectTop         () { return this.configSystemService.getValueAsInteger(ConfigSystemParams.GridLastDataSessionsSelectTop, 100); }
  
  private gridViewDefaults!: GridViewDefaults;
  private showAllRecords   : boolean=false;
  
  public curUser           : SystemUserDTO;
  public dgApiPath!        : DgApiPath | undefined;
  public dgActionColumn!   : DgActionColumn | undefined;
  public dgColumns!        : Array<DgColumn>;
  public dgPopup!          : DgPopup | undefined;
  public dgProperties!     : DgProperties | undefined;
  public endDate           : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  public filterForm!       : UntypedFormGroup;
  public gotDgProperties   : boolean=false;
  public startDate         : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  
  constructor(
    private authenticationService     : AuthenticationService,
    private componentAccessUserService: ComponentAccessUserService,
    private configSystemService       : ConfigSystemService,
    private encryptionService         : EncryptionService,
    private exceptionsService         : ExceptionsService,
    private formBuilder               : UntypedFormBuilder,
    private gridViewService           : GridViewService,
    private validatorService          : ValidatorService
  ) {
    this.curUser         =this.authenticationService.currentUserObject();
    this.gridViewDefaults=new GridViewDefaults(this.authenticationService, this.componentAccessUserService, this.configSystemService, this.exceptionsService);
  }

  public async ngOnInit(): Promise<void> {
    try {
      if (this.orderID==undefined || this.orderID<=0) {
        throw new Error("SimDataSessionsEmbeddedComponent - OrderID cannot be determined");
      }

      this.setUpForm();

      await this.setUpDgApiPaths();
      this.setUpDgActionColumn();
      this.setUpDgColumns();
      this.setUpDgPopup();
      this.setUpDgProperties();

      this.gotDgProperties=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public async ngAfterViewInit(): Promise<void> {
    try {
      this.showAllRecords=false;

      if (!this.dgApiPath)
        await this.setUpDgApiPaths();

      await this.setDgApiPathGet();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private async setUpDgApiPaths(): Promise<void> {
    try {
      this.dgApiPath            =this.gridViewDefaults.setUpDgApiPaths(apiPath);
      this.dgApiPath!.refreshGet=() => this.buildDgApiPathGet();

      const orderIdToEncrypt="0";
      const orderIdEncResult=await firstValueFrom(this.encryptionService.encrypt_AesServerSideForWeb(orderIdToEncrypt, true));
      const orderIdEncSS    =orderIdEncResult.result;

      this.dgApiPath!.get=apiPath + '/' +
                          eSimApiPaths.GetOrderDataSessionsByDate +
                          `?orderIdEncSS=${orderIdEncSS}&fromDate=2000-01-01&toDate=2000-01-01&rowsToReturn=0`;
      
      //CRN_20250708 - called in ngAfterViewInit()
      //await this.setDgApiPathGet();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgActionColumn() {
    try {
      this.dgActionColumn         =this.gridViewDefaults.setUpDgActionColumn();
      this.dgActionColumn!.visible=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgPopup() {
    try {
      this.dgPopup=this.gridViewDefaults.setUpDgPopup(popUpTitle);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgProperties() {
    try {
      this.dgProperties               =this.gridViewDefaults.setUpDgProperties("", exportFilenameSuffix + "-" + this.orderID);
      this.dgProperties!.gridKey      ="EmbeddedSimDataSessions";
      this.dgProperties!.height       =this.embeddedGridHeight;
      this.dgProperties!.pageSize     =5;
      this.dgProperties!.showAddButton=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgColumns() {
    try {
      let dgColumn: DgColumn | undefined;
      
      //OrderID
      dgColumn             =this.gridViewDefaults.setUpDgColumn("orderID", "Order ID", "", DgColumnDataType.string);
      dgColumn!.gridVisible=false;
      this.dgColumns       =[dgColumn!];
      
      //ICCID
      dgColumn             =this.gridViewDefaults.setUpDgColumn("iccid", "ICCID", "", DgColumnDataType.string);
      dgColumn!.gridVisible=false;
      this.dgColumns.push(dgColumn!);
      
      //SessionID
      dgColumn=this.gridViewDefaults.setUpDgColumn("sessionID", "Session ID", "", DgColumnDataType.string);
      this.dgColumns.push(dgColumn!);

      //Country
      dgColumn       =this.gridViewDefaults.setUpDgColumn("country", "Country", "", DgColumnDataType.string);
      dgColumn!.width=100;
      this.dgColumns.push(dgColumn!);

      //UsageWithMbOrGb
      dgColumn                    =this.gridViewDefaults.setUpDgColumn("usageWithMbOrGb", "Usage", "", DgColumnDataType.string);
      dgColumn!.alignment         =DgAlignment.right;
      dgColumn!.calculateSortValue=(rowData: any) => Number(rowData?.usageMB ?? 0);
      dgColumn!.gridVisible       =false;
      this.dgColumns.push(dgColumn!);

      //UsageGB
      dgColumn             =this.gridViewDefaults.setUpDgColumn("usageGB", "Usage (GB)", "", DgColumnDataType.number);
      dgColumn!.alignment  =DgAlignment.right;
      dgColumn!.gridVisible=false;
      this.dgColumns.push(dgColumn!);

      //UsageMB
      dgColumn           =this.gridViewDefaults.setUpDgColumn("usageMB", "Usage (MB)", "", DgColumnDataType.number);
      dgColumn!.alignment=DgAlignment.right;
      dgColumn!.width    =DgColumnWidths.megabytes;
      this.dgColumns.push(dgColumn!);

      //UsageCost
      dgColumn           =this.gridViewDefaults.setUpDgColumn("usageCost", "Cost (£)", "", DgColumnDataType.number);
      dgColumn!.alignment=DgAlignment.right;
      dgColumn!.width    =DgColumnWidths.money;
      this.dgColumns.push(dgColumn!);

      //UsageCost2DP
      dgColumn             =this.gridViewDefaults.setUpDgColumn("usageCost2DP", "Cost", "", DgColumnDataType.number);
      dgColumn!.alignment  =DgAlignment.right;
      dgColumn!.gridVisible=false;
      dgColumn!.width      =DgColumnWidths.money;
      this.dgColumns.push(dgColumn!);

      //UsageCostWithCurrencySymbol
      dgColumn                    =this.gridViewDefaults.setUpDgColumn("usageCostWithCurrencySymbol", "Cost", "", DgColumnDataType.string);
      dgColumn!.alignment         =DgAlignment.right;
      dgColumn!.calculateSortValue=(rowData: any) => Number(rowData?.usageCost ?? 0);
      dgColumn!.gridVisible       =false;
      dgColumn!.width             =DgColumnWidths.money;
      this.dgColumns.push(dgColumn!);

      //LastUsage
      dgColumn       =this.gridViewDefaults.setUpDgColumn("lastUsage", "Last Usage", "", DgColumnDataType.datetime);
      dgColumn!.width=150;
      this.dgColumns.push(dgColumn!);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public gridAddButtonClick(e:any) {
    try {
      
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public gridEditButtonClick(e:any) {
    try {

    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public async startDateChanged(startDate: OurDate): Promise<void> {
    try {
      this.startDate=startDate;

      await this.setDgApiPathGet();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public async endDateChanged(endDate: OurDate): Promise<void> {
    try {
      this.endDate=endDate;

      await this.setDgApiPathGet();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async buildDgApiPathGet(): Promise<void> {
    try {
      if (this.orderID==undefined || this.orderID<=0)
        throw new Error("SimDataSessionsEmbeddedComponent - OrderID cannot be determined");

      const orderIdToEncrypt=<string><unknown>this.orderID;
      const orderIdEncResult=await firstValueFrom(this.encryptionService.encrypt_AesServerSideForWeb(orderIdToEncrypt, true));
      const orderIdEncSS    =orderIdEncResult.result;

      this.dgApiPath!.get=environment.eSimApi +
                          eSimApiPaths.Orders + '/' +
                          eSimApiPaths.GetOrderDataSessionsByDate +
                          "?orderIdEncSS=" + orderIdEncSS +
                          `&fromDate=${this.startDate?.year ?? 1}-${this.startDate?.month ?? 1}-${this.startDate?.day ?? 1}` +
                          `&toDate=${this.endDate?.year ?? 1}-${this.endDate?.month ?? 1}-${this.endDate?.day ?? 1}` +
                          `&rowsToReturn=${this.showAllRecords ? -1 : this.filterForm.get('selecttop')?.value ?? this.selectTop}`;

      this.gridViewService.apiPathGet=this.dgApiPath!.get;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public async setDgApiPathGet(): Promise<void> {
    try {
      await this.buildDgApiPathGet();

      this.gridView?.refreshData();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Form

  get formOfFilterForm() {
    return this.filterForm.controls;
  }
  
  private setUpForm() {
    try {
      this.buildForm();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildForm() {
    try {
      this.removeFormFields(this.filterForm);

      this.filterForm=this.formBuilder.group({ });
      
      this.addFormFields(this.filterForm);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  private addFormFields(formGroup: UntypedFormGroup) {
    try {
      if (formGroup==this.filterForm) {
        this.addFormControl(formGroup, "allRecords", false,          []);
        this.addFormControl(formGroup, "selecttop",  this.selectTop, [this.validatorService.greaterThanZeroValidator]);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private addFormControl(form      : UntypedFormGroup,
                         dataField : string,
                         value     : boolean | number | string | undefined,
                         validators: Validators) {
    try {
      form.addControl(dataField, new FormControl(
        value,
        validators
      ));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  private removeFormFields(form: UntypedFormGroup) {
    try {
      if (form != undefined) {
        for (let control in form.controls) {
          form.removeControl(control);
        }
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public async showAllRecordsClick(): Promise<void> {
    try {
      let checked=this.filterForm.get("allRecords")?.value;

      if (checked != undefined) {
        this.showAllRecords=!checked;

        if (this.showAllRecords) {
          this.filterForm.get("selecttop")?.disable();
          this.filterForm.get('selecttop')?.setValue(undefined);

        } else {
          this.filterForm.controls["selecttop"].enable();
          this.filterForm.get('selecttop')?.setValue(this.selectTop);
        }

        await this.setDgApiPathGet();
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public async validateNumericInput(): Promise<void> {
    try {
      const value  =this.filterForm.get("selecttop")?.value ?? '';
      const cleaned=value.replace(/[^0-9]/g, '');
  
      if (value !== cleaned) {
        this.filterForm.get('selecttop')?.setValue(cleaned);
      }
  
      if (+cleaned > 0) {
        await this.setDgApiPathGet();
      }
  
    } catch (e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
